import type { Editor } from '@tiptap/core';
import { store } from '@/state/store';
import { PAGE_DIMENSIONS, DEFAULT_PAGE_NUMBER } from '@/state/types';
import type { PageSetup, PageNumberSetup } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Stránkovaný náhled (Fáze 2) — vzhled Word Print Layout.
//
// Editace probíhá v jednom kontinuálním editoru; tady se obsah rozřeže na
// reálné A4 listy s vizuálem jako ve Wordu: šedé plátno, bílé listy se
// stínem a mezerami, záhlaví/zápatí uvnitř okrajové zóny stránky (ne jako
// oddělený blok s čárou). Reálná čísla stránek se dosazují do tokenů JS-em,
// protože CSS @page counter na obrazovce nefunguje.
//
// Toolbar má přepínač zobrazení záhlaví/zápatí (jako Word) a stránkuje se
// znovu po přepnutí, protože se mění dostupná výška těla.
// ═══════════════════════════════════════════════════════════════

/** Otevře stránkovaný náhled aktuálního dokumentu v novém okně. */
export function openPrintPreview(editor: Editor): void {
  const s = store.get();
  const hasHF = !!(s.doc.headerHtml?.trim() || s.doc.footerHtml?.trim()) ||
    !!(s.doc.pageNumber && s.doc.pageNumber.enabled);
  const html = buildPreviewDoc(
    editor.getHTML(),
    s.doc.headerHtml || '',
    s.doc.footerHtml || '',
    s.doc.pageSetup,
    s.doc.pageNumber || DEFAULT_PAGE_NUMBER,
    s.doc.name || 'Dokument',
    hasHF,
  );

  const win = window.open('', '_blank');
  if (!win) {
    alert('Náhled se nepodařilo otevřít — povolte prosím vyskakovací okna.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

function buildPreviewDoc(
  bodyHtml: string,
  headerHtml: string,
  footerHtml: string,
  page: PageSetup,
  pageNumber: PageNumberSetup,
  title: string,
  hfOn: boolean,
): string {
  const dims = PAGE_DIMENSIONS[page.size];
  const landscape = page.orientation === 'landscape';
  const wmm = landscape ? dims.h : dims.w;
  const hmm = landscape ? dims.w : dims.h;
  const m = page.margins;

  // Word umisťuje záhlaví/zápatí do okrajové zóny. Použijeme menší „header
  // distance" (vzdálenost od horního/dolního okraje papíru), zbytek okraje je
  // pro tělo. Typické wordovské hodnoty: 12.5 mm od kraje.
  const hfDist = Math.min(12.5, m.top - 2, m.bottom - 2);

  const payload = {
    body: bodyHtml,
    header: headerHtml,
    footer: footerHtml,
    wmm, hmm,
    mt: m.top, mr: m.right, mb: m.bottom, ml: m.left,
    hfDist,
    pageNumber,
    hfOn,
  };

  return `<!DOCTYPE html>
<html lang="cs">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)} — náhled</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  /* Wordovské šedé plátno */
  body { background: #e4e4e4; font-family: 'Times New Roman', serif; font-size: 11pt; color: #000; }

  .pv-toolbar {
    position: sticky; top: 0; z-index: 10; background: #2b579a; color: #fff;
    padding: 7px 16px; display: flex; gap: 10px; align-items: center;
    font-family: 'Segoe UI', system-ui, sans-serif; font-size: 13px;
    box-shadow: 0 1px 4px rgba(0,0,0,.25);
  }
  .pv-toolbar button {
    background: rgba(255,255,255,.14); color: #fff; border: 1px solid rgba(255,255,255,.25);
    border-radius: 4px; padding: 5px 12px; cursor: pointer; font-size: 13px;
  }
  .pv-toolbar button:hover { background: rgba(255,255,255,.24); }
  .pv-toolbar button.on { background: #fff; color: #2b579a; font-weight: 600; }
  .pv-spacer { flex: 1; }
  .pv-hint { opacity: .85; font-size: 12px; }

  .pv-scroll { padding: 28px 0 40px; display: flex; flex-direction: column; align-items: center; gap: 18px; }

  /* List = papír. Padding = okraje dokumentu. Uvnitř absolutně umístěné
     záhlaví (nahoře v okraji) a zápatí (dole v okraji), tělo mezi okraji. */
  .pv-sheet {
    width: ${wmm}mm; height: ${hmm}mm; background: #fff;
    box-shadow: 0 1px 5px rgba(0,0,0,.28), 0 6px 18px rgba(0,0,0,.14);
    padding: ${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm;
    position: relative; overflow: hidden;
  }
  .pv-body { height: 100%; overflow: hidden; }

  /* Záhlaví/zápatí v okrajové zóně, zarovnané na levý/pravý okraj těla */
  .pv-hf {
    position: absolute; left: ${m.left}mm; right: ${m.right}mm;
    font-size: 10.5pt; color: #000;
  }
  .pv-head { top: ${hfDist}mm; }
  .pv-foot { bottom: ${hfDist}mm; }
  .pv-hf p { margin: 0; }
  .pv-hf.hidden, .pv-body.no-hf-adjust {}
  body.hf-off .pv-hf { display: none; }

  /* Obsah těla */
  .pv-body p { margin: 0 0 .35em; }
  .pv-body h1 { font-size: 20pt; margin: .3em 0 .4em; font-weight: 700; }
  .pv-body h2 { font-size: 16pt; margin: .3em 0 .4em; font-weight: 700; }
  .pv-body h3 { font-size: 13pt; margin: .3em 0 .3em; font-weight: 700; }
  .pv-body img { max-width: 100%; }
  .pv-body table { border-collapse: collapse; }
  .pv-body td, .pv-body th { border: .5px solid #999; padding: 3px 6px; }
  .pv-body ul, .pv-body ol { margin: 0 0 .35em; padding-left: 1.4em; }
  .doc-field-chip { background: transparent !important; color: inherit !important; padding: 0 !important; }

  @media print {
    body { background: #fff; }
    .pv-toolbar { display: none; }
    .pv-scroll { padding: 0; gap: 0; }
    .pv-sheet { box-shadow: none; break-after: page; }
    .pv-sheet:last-child { break-after: auto; }
  }
</style>
</head>
<body>
  <div class="pv-toolbar">
    <button onclick="window.print()">Tisk / Uložit jako PDF</button>
    <button id="btn-hf" class="on" title="Zobrazit záhlaví a zápatí">Záhlaví a zápatí</button>
    <div class="pv-spacer"></div>
    <span class="pv-hint" id="hint">Stránkuji…</span>
    <button onclick="window.close()">Zavřít</button>
  </div>
  <div class="pv-scroll" id="scroll"></div>

  <script id="payload" type="application/json">${escapeJson(JSON.stringify(payload))}</script>
  <script>
  (function () {
    var data = JSON.parse(document.getElementById('payload').textContent);
    var scroll = document.getElementById('scroll');
    var btnHF = document.getElementById('btn-hf');
    var hint = document.getElementById('hint');
    var hfOn = !!data.hfOn;

    // Má dokument vůbec nějaké záhlaví/zápatí? Když ne, přepínač skryjeme.
    var hasHF = !!(strip(data.header) || strip(data.footer) ||
      (data.pageNumber && data.pageNumber.enabled));
    if (!hasHF) { btnHF.style.display = 'none'; hfOn = false; }

    function strip(h) { var d = document.createElement('div'); d.innerHTML = h || ''; return (d.textContent || '').trim() || d.querySelector('[data-field]'); }
    function el(tag, cls) { var e = document.createElement(tag); if (cls) e.className = cls; return e; }
    function mm(v) { return v + 'mm'; }

    function paginate() {
      scroll.innerHTML = '';
      document.body.classList.toggle('hf-off', !hfOn);
      btnHF.classList.toggle('on', hfOn);

      var sheets = [];
      function makeSheet() {
        var sheet = el('div', 'pv-sheet');
        var body = el('div', 'pv-body');
        sheet.appendChild(body);
        // Záhlaví/zápatí jen když jsou zapnuté a existují.
        var head = null, foot = null;
        if (hfOn) {
          head = el('div', 'pv-hf pv-head'); head.innerHTML = data.header || '';
          foot = el('div', 'pv-hf pv-foot'); foot.innerHTML = data.footer || '';
          sheet.appendChild(head); sheet.appendChild(foot);
        }
        scroll.appendChild(sheet);
        return { sheet: sheet, body: body, head: head, foot: foot };
      }

      var src = document.createElement('div');
      src.innerHTML = data.body || '<p></p>';
      var blocks = Array.prototype.slice.call(src.childNodes).filter(function (n) {
        return n.nodeType === 1 || (n.nodeType === 3 && n.textContent.trim());
      });

      var cur = makeSheet(); sheets.push(cur);

      function bodyFits(bodyEl) {
        // Dostupná výška těla: klientská výška listu − padding − (pokud jsou HF
        // zapnuté) rezerva na záhlaví/zápatí, aby se tělo nepřekrývalo s nimi.
        var sheetEl = bodyEl.parentElement;
        var cs = getComputedStyle(sheetEl);
        var padT = parseFloat(cs.paddingTop), padB = parseFloat(cs.paddingBottom);
        var avail = sheetEl.clientHeight - padT - padB;
        if (hfOn) {
          var hh = sheetEl.querySelector('.pv-head');
          var fh = sheetEl.querySelector('.pv-foot');
          if (hh) avail -= Math.max(0, hh.offsetHeight - 6);
          if (fh) avail -= Math.max(0, fh.offsetHeight - 6);
        }
        return bodyEl.scrollHeight <= avail + 1;
      }

      for (var i = 0; i < blocks.length; i++) {
        var node = blocks[i];
        if (node.nodeType === 1 && node.classList && node.classList.contains('doc-page-break')) {
          cur = makeSheet(); sheets.push(cur);
          continue;
        }
        var clone = node.cloneNode(true);
        cur.body.appendChild(clone);
        if (!bodyFits(cur.body)) {
          if (cur.body.childNodes.length > 1) {
            cur.body.removeChild(clone);
            cur = makeSheet(); sheets.push(cur);
            cur.body.appendChild(clone);
          }
        }
      }

      var total = sheets.length;
      sheets.forEach(function (s, idx) {
        var pageNo = idx + 1;
        if (s.head) fillFields(s.head, pageNo, total);
        if (s.foot) fillFields(s.foot, pageNo, total);
        applyAutoNumber(s, pageNo);
      });

      hint.textContent = total + ' ' + plural(total);
    }

    function fillFields(region, pageNo, total) {
      var fields = region.querySelectorAll('[data-field]');
      Array.prototype.forEach.call(fields, function (f) {
        var kind = f.getAttribute('data-field');
        f.textContent = kind === 'pages' ? String(total) : String(pageNo);
      });
    }

    function applyAutoNumber(s, pageNo) {
      var pn = data.pageNumber;
      if (!pn || !pn.enabled) return;
      var region = pn.place === 'header' ? s.head : s.foot;
      if (!region) return;
      if (region.querySelector('[data-field]')) return;
      // Nepřidávej duplicitně, když už tam auto-číslo je.
      if (region.querySelector('.pv-auto-num')) return;
      var p = document.createElement('p');
      p.className = 'pv-auto-num';
      p.style.textAlign = pn.align || 'center';
      p.textContent = String(pageNo);
      region.appendChild(p);
    }

    function plural(n) { return n === 1 ? 'stránka' : (n >= 2 && n <= 4 ? 'stránky' : 'stránek'); }

    btnHF.addEventListener('click', function () { hfOn = !hfOn; paginate(); });

    paginate();
  })();
  </script>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Bezpečné vložení JSON do <script> (rozbití uzavírací značky).
function escapeJson(s: string): string {
  return s.replace(/<\/script/gi, '<\\/script').replace(/<!--/g, '<\\!--');
}
